import {observer} from 'mobx-react-lite'
import React, {useCallback, useEffect, useRef, useState} from 'react'
import tw from 'tailwind-styled-components'
import {Lang} from 'yougile-lang'
import {DataObjectId} from 'yougile-model'
import {addingAllowed} from '@client/hub-actions-allowed'
import {Sync} from '@client/sync'
import {ExtensionsInColumn} from '@components/columns'
import {useColumnStore} from '@components/columns/column-store'
import {LinkButtonNew} from '@components/common/base/buttons'
import {IconTabler} from '@components/common/icon-tabler/icon-tabler'
import {crmStore} from '@components/crm/crm-store'
import {TaskTemplatesMenu} from '@components/task-menu/task-templates-menu'
import {noPropagate} from '@containers/hooks'
import {
  getScrollYToCenterDelta,
  getScrollYToTopDelta,
  isVisibleYInParent
} from '@containers/scroll-utils'
import {useClickOut} from '../click-out'
import {AddTaskContext, AddTaskStore, useAddTaskStore} from './add-task-store'
import {AddTaskWithRequiredStickers} from './add-task-with-required-stickers'
import {TextEdit} from './text-edit'
import {AddTaskProps} from './types'

const TwAddTaskButton = tw(LinkButtonNew)`
  ml-8
`

const TwAddTaskButtons = tw.div`
  flex
  items-center
  justify-between
  pr-4
  -mx-8
`

const TwAddTask = tw.div`
  flex
  gap-6
`

// todo: remove mb-4 after refactoring a margin between column title&column when scrolling
const TwAddTaskBase = tw.div`
  relative
  rounded-8
  -mx-8
  bg-background-card
  shadow-xs
  p-8
`

const useScrollTo = (
  scrollTo?: 'start' | 'end' | 'task-center' | 'task-top'
): void => {
  const [, store] = useAddTaskStore()
  const [columnState] = useColumnStore()
  const scrollableEl = columnState.scrollableEl

  useEffect(() => {
    if (!scrollTo) {
      return () => void 0
    }

    // Small timeout is required for other rerenders to happen
    const scrollToTask = (taskId?: DataObjectId) => {
      setTimeout(() => {
        if (scrollableEl && taskId && scrollTo) {
          if (scrollTo === 'start') {
            scrollableEl.scrollTop = 0
          } else if (scrollTo === 'end') {
            scrollableEl.scrollTop = Number.MAX_SAFE_INTEGER
          } else if (['task-center', 'task-top'].includes(scrollTo)) {
            const taskEl = scrollableEl.querySelector(
              `[data-task-id="${taskId}"]`
            )
            if (taskEl) {
              if (scrollTo === 'task-center') {
                const dy = getScrollYToCenterDelta(scrollableEl, taskEl)
                scrollableEl.scrollTop += dy
              } else if (scrollTo === 'task-top') {
                const dy = getScrollYToTopDelta(scrollableEl, taskEl)
                scrollableEl.scrollTop += dy
              }
            }
          }
        }
      }, 300)
    }

    store.setOnAdd(scrollToTask)
    return () => store.setOnAdd(undefined)
  }, [store, scrollableEl, scrollTo])
}

const AddTaskBase = observer(
  ({toEnd, className, scrollTo, columnId}: AddTaskProps) => {
    const ref = useRef<HTMLElement>(null)
    const [state, store] = useAddTaskStore()
    const [columnState] = useColumnStore()
    const isTextEditShowed = state.isTextEditShowed
    const scrollableEl = columnState.scrollableEl

    // ClickOut as a hook to allow using an element ref outside the ClickOut
    const onClickOut = useCallback(
      (e: MouseEvent) => {
        if (store.state.isTextEditShowed) {
          e
          // отключил перестают работать клики по другим задачам
          // с открытым инупутом добавить задачу
          // e.stopPropagation()
          // e.preventDefault()
          store.createTaskOnBlur(toEnd)
        }
      },
      [store, toEnd]
    )
    useClickOut({ref, cb: onClickOut, capture: true, type: 'click'})

    // Scroll to a task, or to a top/bottom of a scrollable column if required
    useScrollTo(scrollTo)

    // Scroll to add task element if it's in a scrollable column
    useEffect(() => {
      const el = ref.current
      if (
        isTextEditShowed &&
        el &&
        scrollableEl?.contains(el) &&
        !isVisibleYInParent(scrollableEl, el)
      ) {
        const dy = getScrollYToCenterDelta(scrollableEl, el)
        scrollableEl.scrollTop += dy
      }
    }, [scrollableEl, ref, isTextEditShowed])

    if (!isTextEditShowed) {
      store.createTask()
      const isCrmBoard = crmStore.isCrmBoard(store.board)
      const onClickAddTask = isCrmBoard
        ? () => crmStore.showNewDealPopup(store.state.columnId)
        : store.show

      return (
        <TwAddTaskButtons className={className}>
          <TwAddTaskButton
            icon="IconPlus"
            color="primary"
            onClick={onClickAddTask}
            title={Lang.context.board.addTask}
          />

          <TwAddTask>
            <ExtensionsInColumn boardId={store.board?.id} columnId={columnId} />
            {!isCrmBoard && <TaskTemplatesMenu boardId={store.board?.id} />}
          </TwAddTask>
        </TwAddTaskButtons>
      )
    }

    return (
      <TwAddTaskBase ref={ref} className={className} onMouseDown={noPropagate}>
        <IconTabler
          icon="IconCustomTaskDoneEmpty"
          className="absolute top-8 left-8 text-icon-placeholder"
        />
        {store.hasRequiredStickers || state.templateId ? (
          <>
            <TextEdit small toEnd={toEnd} />
            <AddTaskWithRequiredStickers />
          </>
        ) : (
          <TextEdit toEnd={toEnd} />
        )}
      </TwAddTaskBase>
    )
  }
)

export const AddTask = (props: AddTaskProps) => {
  const {columnId} = props

  const box = Sync.getCurrentSandbox()
  const column = box.getHub(columnId)
  const board = box.getHub(column?.location)

  const [store] = useState(() => new AddTaskStore(board, columnId))

  useEffect(() => {
    store.syncOnUpdate(columnId)
  }, [store, columnId])

  if (!column || !addingAllowed(columnId)) {
    return null
  }

  return (
    <AddTaskContext.Provider value={store}>
      <AddTaskBase {...props} />
    </AddTaskContext.Provider>
  )
}
